import { message } from 'antd';
import _logger from 'utils/logger';
import API from 'api/index';
import { formatReceived, formatSent, hasFiles } from 'utils/formatters';
import { updateUserInfo, selectEntidad, selectPublicoGeneral } from 'store/auth';
import store from 'store/store';
import { selectContentTypesPadrones, selectPadrones, setPadrones } from 'store/catalogos';
import {
  ENV_NAME,
  ENV_NAMES_ENUM,
  ID_ENTIDAD,
  PROD,
  REPUVE,
} from 'utils/env';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { postEmpresa } from './empresas/empresas';

const descriptionsMap = {
  4: 'Vehículo',
  11: 'Juegos de Azar',
  13: 'Casa de empeño',
};

const files = ['foto'];

const isAvailable = (padron) => {
  if (ENV_NAME === ENV_NAMES_ENUM.JUAREZ) {
    if (PROD) {
      return ![
        padronesEnum.PREDIO,
        padronesEnum.INFRACCIONES,
        padronesEnum.MULTAS,
      ].includes(padron);
    }
    return true;
  }
  if (ENV_NAME === ENV_NAMES_ENUM.NAYARIT) {
    return [
      padronesEnum.CIUDADANO,
      padronesEnum.PREDIO,
      padronesEnum.VEHICULO,
      padronesEnum.ALCOHOL,
      padronesEnum.CONTRIBUYENTE,
      padronesEnum.MOTOCICLETA,
      padronesEnum.INFRACCIONES,
      padronesEnum.CASA_DE_EMPENIO,
    ].includes(padron);
  }
  return false;
};

/**
 * GET Tipos de Padrones
 * @param {func} dispatch
 * @returns {Promise<object[]>}
 */
export const getPadrones = async (dispatch) => {
  try {
    const response = await API.get('catalogos/padrones');
    const mapped = response.data.filter((e) => e.id !== 5).map((e) => ({
      ...e,
      descripcion: descriptionsMap[e.id] || e.descripcion,
      available: isAvailable(e.id),
    }));
    if (dispatch) {
      dispatch(setPadrones(mapped));
    }
    return mapped;
  } catch (error) {
    _logger(error);
  }
  return [];
};

/**
 * @param {object} obj Object to evaluate
 * @returns object[]
 */
const createFilterWithObject = (obj = {}) => (prop) => !obj?.[prop];

const propsToEvaluateByObject = {
  ciudadano: ['email', 'numero_de_celular'],
  empresa: ['correo_electronico', 'numero_de_celular'],
  contribuyente: ['correo_electronico', 'telefono_celular'],
};

export const propsToRequire = {
  ciudadano: [...propsToEvaluateByObject.ciudadano, 'lada'],
  empresa: [...propsToEvaluateByObject.empresa, 'lada_celular'],
  contribuyente: [...propsToEvaluateByObject.contribuyente, 'lada_celular'],
};

const ciudadanoPadronDerivadoDeEmpresa = (padron) => padron.empresa?.ciudadano?.id;

const findCiudadanoIntoPadronFuncMap = {
  1: (padron) => padron.id,
  2: (padron) => padron.ciudadano?.id,
  3: (padron) => padron.ciudadano?.id,
  4: (padron) => padron.propietario?.id,
  15: (padron) => padron.ciudadano?.id,
  16: (padron) => padron.ciudadano_propietario?.id,
  17: (padron) => padron?.ciudadano_propietario?.id,
};

export const findCiudadano = (padron, tipoDePadron) => {
  const findFunc = findCiudadanoIntoPadronFuncMap[tipoDePadron] || ciudadanoPadronDerivadoDeEmpresa;
  const ciudadano = findFunc(padron);
  const publicoGeneral = selectPublicoGeneral(store.getState());
  return ciudadano || publicoGeneral?.id || publicoGeneral || null;
};

const neededInfoIn = (
  padron,
  propNameContribuyente,
  propNameCiudadano,
  validateSelf = false,
) => {
  if (!padron) {
    return [];
  }
  const contribuyenteHasAllContactInfo = !propsToEvaluateByObject.contribuyente
    .filter(createFilterWithObject(padron[propNameContribuyente])).length;
  if (propNameContribuyente && padron[propNameContribuyente]) {
    // si contribuyente tiene toda la info de contacto se usa esa
    if (contribuyenteHasAllContactInfo) {
      return [false, null, null, propNameContribuyente];
    }
    // si no hay ciudadano y no se buscara en el mismo padron
    // se necesita actualizar informacion de contribuyente
    if (propNameCiudadano && !padron.ciudadano && !validateSelf) {
      return [true, padron[propNameContribuyente].id, 15];
    }
  }
  if (propNameCiudadano && padron[propNameCiudadano]) {
    const ciudadanoHasAllContactInfo = !propsToEvaluateByObject.ciudadano
      .filter(createFilterWithObject(padron[propNameCiudadano])).length;
    // si ciudadano tiene toda la info de contacto se usa esa
    if (ciudadanoHasAllContactInfo) {
      return [false, null, null, propNameCiudadano];
    }
    // si hay contribuyente y no se buscara en el mismo padron
    // se prioriza actualizar informacion de contribuyente
    if (propNameContribuyente && padron[propNameContribuyente] && !validateSelf) {
      return [true, padron[propNameContribuyente].id, 15];
    }
    // si no se buscara en el mismo padron se necesita actualizar informacion de ciudadano
    if (!validateSelf) {
      return [true, padron[propNameCiudadano].id, 1];
    }
  }
  // si se puede buscar info en el mismo padron (solo aplica empresa)
  if (validateSelf) {
    const hasAllContactInfo = !propsToEvaluateByObject.empresa
      .filter(createFilterWithObject(padron)).length;
    // si se tiene toda la info directamente se usa esa
    if (hasAllContactInfo) {
      return [false, null, null, validateSelf];
    }
    // sino volvemos a validar pero excluyendo info directa del padron
    return neededInfoIn(padron, propNameContribuyente, propNameCiudadano);
  }
  // no hay relacion a propietario ni info directamente en el padron
  return [];
};

const infoNeededByPadronTypeMap = {
  1: (padron) => {
    const hasAllContactInfo = !propsToEvaluateByObject.ciudadano
      .filter(createFilterWithObject(padron)).length;
    if (hasAllContactInfo) {
      return [false, null, null, 'self'];
    }
    return [true, padron.id, 1];
  },
  2: (padron) => neededInfoIn(padron, null, 'ciudadano', false),
  3: (padron) => neededInfoIn(padron, 'contribuyente', 'ciudadano', false),
  4: (padron) => neededInfoIn(padron, 'contribuyente_propietario', 'propietario', false),
  6: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  7: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  8: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  9: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  10: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  11: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  12: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  13: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  14: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  15: (padron) => {
    const contribuyenteHasAllContactInfo = !propsToEvaluateByObject.contribuyente
      .filter(createFilterWithObject(padron)).length;
    if (contribuyenteHasAllContactInfo) {
      return [false, null, null, 'self'];
    }
    // si no hay ciudadano y no se buscara en el mismo padron
    // se necesita actualizar informacion de contribuyente
    if (padron.ciudadano && padron.tipo_de_persona === 1) {
      const ciudadanoHasAllContactInfo = !propsToEvaluateByObject.ciudadano
        .filter(createFilterWithObject(padron.ciudadano)).length;
      if (ciudadanoHasAllContactInfo) {
        return [false, null, null, 'ciudadano'];
      }
    }
    return [true, padron.id, 15];
  },
  16: (padron) => neededInfoIn(padron, 'contribuyente_propietario', 'propietario', false),
  17: (padron) => neededInfoIn(padron, 'contribuyente_propietario', 'propietario', false),
  18: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  19: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  20: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  21: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  22: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  23: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  24: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  25: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
  26: (padron) => neededInfoIn(padron.empresa, 'contribuyente', 'ciudadano', 'empresa'),
};

const validateResult = (
  _data,
  tipoDePadron,
  useRegisterMessage = false,
) => {
  let data = _data?.results || _data || [];
  // motocicletas suspendidas no se busca
  if (tipoDePadron === 16 && data[0]?.estatus_del_vehiculo === 10) {
    data = [];
  }
  if (data.length) {
    if (data.length === 1) {
      const [
        infoNeeded = false,
        idPadronUpdate,
        tipoDePadronNeedsInfo,
        propNameContactInfo,
      ] = infoNeededByPadronTypeMap[tipoDePadron](data[0]);
      return {
        ...data[0],
        infoNeeded,
        idPadronUpdate,
        tipoDePadronNeedsInfo,
        propNameContactInfo,
      };
    }
    message.info('Intente con una búsqueda más específica');
  } else {
    const padronString = selectPadrones(store.getState())
      .find((e) => e.id === tipoDePadron)?.descripcion || '';
    const defaultMessage = `No se ha encontrado ${padronString}, verifique la información`;
    const registerMessage = ' o registrese';
    message.info(`${defaultMessage}${useRegisterMessage ? registerMessage : ''}`);
  }
  return null;
};

/**
 * GET Búsqueda en Padrón Ciudadanos
 * @param {object} params
 * @param {string} params.q
 * @param {boolean} useRegisterMessage
 * @returns {Promise<object[]>}
 */
export const getCiudadano = async (validate, params, useRegisterMessage = false) => {
  try {
    const response = await API.get('cuentaunicasir/ciudadano-caja-public', { params });
    return validate
      ? validateResult(response.data, 1, useRegisterMessage) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Empresa
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getEmpresa = async (validate, params) => {
  try {
    const response = await API.get('cuentaunicasir/empresas-caja-atencion-ciudadana/', { params });
    return validate ? validateResult(response.data, 2) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Predio
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getPredio = async (validate, params) => {
  try {
    const _publicoGeneral = selectEntidad(store.getState());
    const response = await API.get('cuentaunicasir/predio-caja-atencion-ciudadana/', { params });
    if (response.status === 204) {
      return validate ? null : [];
    }
    return validate
      ? validateResult(response.data, 3, false, _publicoGeneral) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Vehiculo
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getVehiculo = async (validate, params) => {
  try {
    const response = await API.get(REPUVE ? 'cuentaunicasir/vehiculos-caja-atencion-ciudadana' : 'cuentaunicasir/vehiculos-portal', { params });
    return validate ? validateResult(response.data, 4) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Hospedaje
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getHospedaje = async (validate, params) => {
  try {
    const response = await API.get('empresas/hospedaje-caja-public/', { params });
    return validate ? validateResult(response.data, 6) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Arrendamiento
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getArrendamiento = async (validate, params) => {
  try {
    const response = await API.get('empresas/arrendamiento-caja-public/', { params });
    return validate ? validateResult(response.data, 7) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Nomina
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getNomina = async (validate, params) => {
  try {
    const response = await API.get('empresas/nomina-caja-public/', { params });
    return validate ? validateResult(response.data, 8) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Nomina Retrive
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getNominaContribuyente = async (id) => {
  try {
    const response = await API.get(`empresas/nomina-caja-public/${id}`);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return [];
};

export const getRetrive = async (id, name) => {
  try {
    const response = await API.get(`empresas/${name}-caja-public/${id}`);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return [];
};

// RECIBE EL ID DE LA EMPRESA Y EL NOMBRE DEL PADRON Y ID DE LA CLASE DE DECLARACION
export const getRetriveV2 = async ({ id, name, clase_de_declaracion_empresarial }) => {
  try {
    const response = await API.get(`empresas/${name}/${id}`, {
      params: { clase_de_declaracion_empresarial },
    });
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return [];
};

/**
 * GET Búsqueda en Padrón Nomina Retencion
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getNominaRetencion = async (validate, params) => {
  try {
    const response = await API.get('empresas/retenedor-de-nomina-caja-public/', { params });
    return validate ? validateResult(response.data, 8) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Alcohol
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getAlcohol = async (validate, params) => {
  try {
    const response = await API.get('empresas/alcohol-caja-public/', { params });
    return validate ? validateResult(response.data, 9) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Alcohol
 * @param {object} id
 * @returns {Promise<object[]>}
 */
export const getAlcoholById = async (id) => {
  try {
    const response = await API.get(`empresas/alcohol-caja-public/${id}`);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * GET Búsqueda en Padrón Venta de Alcohol
 * @param {object} id
 * @returns {Promise<object[]>}
 */
export const getVentaAlcoholById = async (id) => {
  try {
    const response = await API.get(`empresas/venta-de-alcohol-caja-public/${id}`);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * GET Búsqueda en Padrón Hospedaje
 * @param {object} id
 * @returns {Promise<object[]>}
 */
export const getHospedajeById = async (validate, params) => {
  try {
    const response = await API.get('empresas/hospedaje-caja-public/', { params });
    return validate ? validateResult(response.data, 6) : response.data;
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * GET Búsqueda en Padrón Notarios
 * @param {object} id
 * @returns {Promise<object[]>}
 */
export const getNotarioseById = async (id) => {
  try {
    const response = await API.get(`empresas/notarios-caja-public/${id}`);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * GET Búsqueda en Padrón Cedular
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getCedular = async (validate, params) => {
  try {
    const response = await API.get('empresas/cedular-caja-public/', { params });
    return validate ? validateResult(response.data, 10) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Cedular Retencion
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getCedularRetencion = async (validate, params) => {
  try {
    const response = await API.get('empresas/retenedor-cedular-caja-public/', { params });
    return validate ? validateResult(response.data, 10) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en venta de alcohol
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getVentaAlcohol = async (validate, params) => {
  try {
    const response = await API.get('empresas/venta-de-alcohol-caja-public/', { params });
    return validate ? validateResult(response.data, 26) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Juegos de Azar
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getJuegoDeAzar = async (validate, params) => {
  try {
    const response = await API.get('empresas/juego-de-azar-caja-public/', { params });
    return validate ? validateResult(response.data, 11) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Notario
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getNotario = async (validate, params) => {
  try {
    const response = await API.get('empresas/notario-caja-public/', { params });
    return validate ? validateResult(response.data, 12) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Caja de Empeño
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getCasaDeEmpenio = async (validate, params) => {
  try {
    const response = await API.get('/empresas/casa-de-empenio-caja-public/', { params });
    return validate ? validateResult(response.data, 13) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Agencia
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getAgencia = async (validate, params) => {
  try {
    const response = await API.get('empresas/agencia-caja-public/', { params });
    return validate ? validateResult(response.data, 14) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

const removeGenerics = (e) => ({
  ...e,
  correo_electronico: e.correo_electronico === 'some@nayarit.com.mx' ? null : e.correo_electronico,
});

/**
 * GET Búsqueda en Padrón Contribuyente
 * @param {object} params
 * @param {string} params.q
 * @param {boolean} useRegisterMessage
 * @returns {Promise<object[]>}
 */
export const getContribuyente = async (
  validate,
  params,
  useRegisterMessage = false,
) => {
  try {
    const response = await API.get('empresas/contribuyentes-caja-public/', { params });
    response.data.results = response.data.results.map(removeGenerics);
    return validate
      ? validateResult(response.data, 15, useRegisterMessage) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Padrón Motocicleta
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getMotocicleta = async (validate, params) => {
  try {
    const response = await API.get('recaudacion/motocicletas-caja-public/', { params });
    return validate ? validateResult(response.data, 16) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Remolque
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getRemolque = async (validate, params) => {
  try {
    const response = await API.get('recaudacion/remolques-caja-public/', { params });
    return validate ? validateResult(response.data, 17) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Policias Especiales
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getPoliciaEspecial = async (validate, params) => {
  try {
    const response = await API.get('empresas/contratos-de-policias-caja-public/', { params });
    return validate ? validateResult(response.data, 18) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Infracciones
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getInfracciones = async (validate, params) => {
  try {
    const response = await API.get('recaudacion/infracciones-caja-public/', { params });
    return validate ? validateResult(response.data, 19) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Expediente de licencia de comercio
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getExpedienteDeComercio = async (validate, params) => {
  try {
    const response = await API.get('empresas/expedientes-de-comercio-informal-caja-public/', { params });
    return validate ? validateResult(response.data, 20) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Expediente de mercado
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getExpedienteDeMercado = async (validate, params) => {
  try {
    const response = await API.get('empresas/expedientes-de-mercados-caja-public/', { params });
    return validate ? validateResult(response.data, 21) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Expediente de licencia de funcionamiento
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getExpedienteDeLicencia = async (validate, params) => {
  try {
    const response = await API.get('empresas/expedientes-de-licencias-caja-public/', { params });
    return validate ? validateResult(response.data, 22) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Zonas exclusivas
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getZonaExclusiva = async (validate, params) => {
  try {
    const response = await API.get('empresas/zonas-exclusivas-caja-public/', { params });
    return validate ? validateResult(response.data, 23) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * GET Búsqueda en Expediente de Anuncios
 * @param {object} params
 * @param {string} params.q
 * @returns {Promise<object[]>}
 */
export const getExpedienteDeAnuncio = async (validate, params) => {
  try {
    const response = await API.get('empresas/expedientes-de-anuncios-caja-public/', { params });
    return validate ? validateResult(response.data, 24) : response.data;
  } catch (error) {
    _logger(error);
  }
  return validate ? null : [];
};

/**
 * POST Padrón Ciudadano
 * @param {object} values
 * @returns {Promise<object | null>}
 */
export const postCiudadano = async (values) => {
  try {
    const response = await API.post('cuentaunicasir/ciudadano-public', { ...values, entidad: [ID_ENTIDAD] });
    message.info('Ciudadano registrado correctamente', 3);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * Patch Padrón Ciudadano
 * @param {number} key
 * @param {object} _values
 * @param {function} dispatch
 * @returns {Promise<object | null>}
 */
export const patchCiudadanoPublic = async (key, _values, dispatch) => {
  try {
    const config = hasFiles(_values, files) ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    const values = {
      ...formatSent(_values, {
        files,
        formData: config.headers,
      }),
    };
    const response = await API.patch(`cuentaunicasir/ciudadano-public/${key}/`, values);
    message.info('Ciudadano actualizado correctamente', 3);
    if (typeof dispatch === 'function') {
      dispatch(updateUserInfo({ user: response.data }));
    }

    return formatReceived(response.data);
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * POST Padrón Contribuyente
 * @param {object} values
 * @returns {Promise<object | null>}
 */
export const postContribuyente = async (values) => {
  try {
    const response = await API.post('empresas/contribuyente-public', { ...values, entidad: 1 });
    message.info('Contribuyente registrado correctamente', 3);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * PATCH Padrón Contribuyente
 * @param {number} key
 * @param {object} values
 * @param {function} dispatch
 * @returns {Promise<object | null>}
 */
export const patchContribuyentePublic = async (key, values, dispatch) => {
  try {
    const response = await API.patch(`empresas/contribuyente-public/${key}/`, values);
    message.info('Contribuyente actualizado correctamente', 3);
    if (typeof dispatch === 'function') {
      dispatch(updateUserInfo({ user: response.data }));
    }
    return formatReceived(response.data);
  } catch (error) {
    _logger(error);
  }
  return null;
};

export const getRegimenesFiscales = async (params) => {
  try {
    const response = await API.get('/empresas/regimen-fiscal/', { params });
    return response.data.map((e) => ({ value: e.id, label: e.descripcion }));
  } catch (err) {
    _logger(err);
    return [];
  }
};

export const getTipoDePadronPorContenType = (contentTypeId) => {
  const contentType = selectContentTypesPadrones(store
    .getState()).find((p) => p.id === contentTypeId);
  return selectPadrones(store.getState()).find((p) => p.id === contentType?.nivel)?.id;
};

export const getContenTypePorTipoDePadron = (tipoDePadronId) => selectContentTypesPadrones(store
  .getState()).find((p) => p.nivel === tipoDePadronId)?.id;

const gettersMap = {
  1: (...args) => getCiudadano(true, ...args),
  2: (...args) => getEmpresa(true, ...args),
  3: (...args) => getPredio(true, ...args),
  4: (...args) => getVehiculo(true, ...args),
  6: (...args) => getHospedaje(true, ...args),
  7: (...args) => getArrendamiento(true, ...args),
  8: (...args) => getNomina(true, ...args),
  9: (...args) => getAlcohol(true, ...args),
  10: (...args) => getCedular(true, ...args),
  11: (...args) => getJuegoDeAzar(true, ...args),
  12: (...args) => getNotario(true, ...args),
  13: (...args) => getCasaDeEmpenio(true, ...args),
  14: (...args) => getAgencia(true, ...args),
  15: (...args) => getContribuyente(true, ...args),
  16: (...args) => getMotocicleta(true, ...args),
  17: (...args) => getRemolque(true, ...args),
  18: (...args) => getPoliciaEspecial(true, ...args),
  19: (...args) => getInfracciones(true, ...args),
  20: (...args) => getExpedienteDeComercio(true, ...args),
  21: (...args) => getExpedienteDeMercado(true, ...args),
  22: (...args) => getExpedienteDeLicencia(true, ...args),
  23: (...args) => getZonaExclusiva(true, ...args),
  24: (...args) => getExpedienteDeAnuncio(true, ...args),
  26: (...args) => getVentaAlcohol(true, ...args), // VENTA DE ALCOHOL
  27: (...args) => getCedularRetencion(true, ...args), // RETENEDOR CEDULAR
  28: (...args) => getNominaRetencion(true, ...args), // RETENEDOR NOMINA
};

/**
 * GET LIST
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const generateGetList = (uri) => async (params) => {
  try {
    const response = await API.get(uri, { params });
    response.data = formatReceived(response.data);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return { results: [] };
};

const injectTipoDePadron = async (func, args, tipo_de_padron) => {
  const response = (typeof func === 'function' ? await func(...args) : (await API.get(func, { params: args[1] })).data);
  if (response.results) {
    return {
      ...response,
      results: response.results.map((e) => ({ ...e, tipo_de_padron })),
    };
  }
  return (response || []).map((e) => ({ ...e, tipo_de_padron }));
};

export const gettersListMap = {
  1: (params) => injectTipoDePadron(getCiudadano, [false, params], 1),
  2: (params) => injectTipoDePadron('empresas/empresas/', [false, params], 2),
  3: (params) => injectTipoDePadron(getPredio, [false, params], 3),
  4: (params) => injectTipoDePadron(getVehiculo, [false, params], 4),
  6: (params) => injectTipoDePadron(getHospedaje, [false, params], 6),
  7: (params) => injectTipoDePadron(getArrendamiento, [false, params], 7),
  8: (params) => injectTipoDePadron(getNomina, [false, params], 8),
  9: (params) => injectTipoDePadron(getAlcohol, [false, params], 9),
  10: (params) => injectTipoDePadron(getCedular, [false, params], 10),
  11: (params) => injectTipoDePadron(getJuegoDeAzar, [false, params], 11),
  12: (params) => injectTipoDePadron(getNotario, [false, params], 12),
  13: (params) => injectTipoDePadron(getCasaDeEmpenio, [false, params], 13),
  14: (params) => injectTipoDePadron(getAgencia, [false, params], 14),
  15: (params) => injectTipoDePadron(getContribuyente, [false, params], 15),
  16: (params) => injectTipoDePadron(getMotocicleta, [false, params], 16),
  17: (params) => injectTipoDePadron(getRemolque, [false, params], 17),
  18: (params) => injectTipoDePadron(getPoliciaEspecial, [false, params], 18),
  19: (params) => injectTipoDePadron(getInfracciones, [false, params], 19),
  20: (params) => injectTipoDePadron(getExpedienteDeComercio, [false, params], 20),
  21: (params) => injectTipoDePadron(getExpedienteDeMercado, [false, params], 21),
  22: (params) => injectTipoDePadron(getExpedienteDeLicencia, [false, params], 22),
  23: (params) => injectTipoDePadron(getZonaExclusiva, [false, params], 23),
  24: (params) => injectTipoDePadron(getExpedienteDeAnuncio, [false, params], 24),
  26: (params) => injectTipoDePadron(getVentaAlcohol, [false, params], 26), // VENTA DE ALCOHOL
  27: (params) => injectTipoDePadron(getCedularRetencion, [false, params], 27), // RETENE CEDULAR
  28: (params) => injectTipoDePadron(getNominaRetencion, [false, params], 28), // RETENEDOR NOMINA
};

/**
 * PATCH Contribuyente
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object | null>}
 */
export const patchContribuyente = async (id, values) => {
  try {
    const response = await API.patch(`empresas/contribuyente-public/${id}/`, values);
    message.info('Contribuyente actualizado correctamente', 3);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * PATCH Contribuyente
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object | null>}
 */
export const patchExpedienteDeLicencia = async (id, values) => {
  try {
    const response = await API.patch(`empresas/contribuyente-public/${id}/`, values);
    message.info('Expediente de licencia actualizado correctamente', 3);
    return response.data;
  } catch (error) {
    _logger(error);
  }
  return null;
};

export const patchPadronesPublicMap = {
  15: patchContribuyente,
  22: patchExpedienteDeLicencia,
};

/**
 * POST
 * @param {object} values
 * @returns {Promise<object | null>}
 */
export const generatePost = (uri) => async (values) => {
  try {
    const response = await API.post(uri, formatSent(values));
    message.info('Actualizado correctamente', 3);
    return formatReceived(response.data);
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * PATCH
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object | null>}
 */
export const generatePatch = (uri) => async (id, values) => {
  try {
    const response = await API.patch(`${uri}/${id}/`, formatSent(values));
    message.info('Actualizado correctamente', 3);
    return formatReceived(response.data);
  } catch (error) {
    _logger(error);
  }
  return null;
};

/**
 * RETRIEVE
 * @param {number} id
 * @returns {Promise<object | null>}
 */
export const generateRetrieve = (uri) => async (id) => {
  try {
    const response = await API.get(`${uri}/${id}/`);
    return formatReceived(response.data);
  } catch (error) {
    _logger(error);
  }
  return null;
};

export const postPadronesPrivateMap = {
  [padronesEnum.VENTA_DE_ALCOHOL]: generatePost('empresas/ventas-de-alcoholes'),
  [padronesEnum.RETENEDOR_DE_CEDULAR]: generatePost('empresas/retenedores-cedulares'),
  [padronesEnum.RETENEDOR_DE_NOMINA]: generatePost('empresas/retenedor-de-nominas'),
  [padronesEnum.AGENCIA]: generatePost('empresas/agencias'),
  [padronesEnum.CASA_DE_EMPENIO]: generatePost('empresas/casas-de-empenio'),
  [padronesEnum.NOTARIO]: generatePost('empresas/notarios'),
  [padronesEnum.JUEGO_DE_AZAR]: generatePost('empresas/juegos-de-azar'),
  [padronesEnum.ALCOHOL]: generatePost('empresas/alcoholes'),
  [padronesEnum.CEDULAR]: generatePost('empresas/cedulares'),
  [padronesEnum.ARRENDAMIENTO]: generatePost('empresas/arrendamientos'),
  [padronesEnum.HOSPEDAJE]: generatePost('empresas/hospedajes'),
  [padronesEnum.AGENCIA]: generatePost('empresas/agencias'),
  [padronesEnum.NOMINA]: generatePost('empresas/nominas'),
  [padronesEnum.CONTRIBUYENTE]: generatePost('empresas/contribuyentes'),
};

export const patchPadronesPrivateMap = {
  [padronesEnum.VENTA_DE_ALCOHOL]: generatePatch('empresas/ventas-de-alcoholes'),
  [padronesEnum.RETENEDOR_DE_CEDULAR]: generatePatch('empresas/retenedores-cedulares'),
  [padronesEnum.RETENEDOR_DE_NOMINA]: generatePatch('empresas/retenedor-de-nominas'),
  [padronesEnum.AGENCIA]: generatePatch('empresas/agencias'),
  [padronesEnum.CASA_DE_EMPENIO]: generatePatch('empresas/casas-de-empenio'),
  [padronesEnum.NOTARIO]: generatePatch('empresas/notarios'),
  [padronesEnum.JUEGO_DE_AZAR]: generatePatch('empresas/juegos-de-azar'),
  [padronesEnum.ALCOHOL]: generatePatch('empresas/alcoholes'),
  [padronesEnum.CEDULAR]: generatePatch('empresas/cedulares'),
  [padronesEnum.ARRENDAMIENTO]: generatePatch('empresas/arrendamientos'),
  [padronesEnum.HOSPEDAJE]: generatePatch('empresas/hospedajes'),
  [padronesEnum.AGENCIA]: generatePatch('empresas/agencias'),
  [padronesEnum.NOMINA]: generatePatch('empresas/nominas'),
  [padronesEnum.CONTRIBUYENTE]: generatePatch('empresas/contribuyentes'),
};

export const retrievePadronesPrivateMap = {
  [padronesEnum.VENTA_DE_ALCOHOL]: generateRetrieve('empresas/ventas-de-alcoholes'),
  [padronesEnum.RETENEDOR_DE_CEDULAR]: generateRetrieve('empresas/retenedores-cedulares'),
  [padronesEnum.RETENEDOR_DE_NOMINA]: generateRetrieve('empresas/retenedor-de-nominas'),
  [padronesEnum.AGENCIA]: generateRetrieve('empresas/agencias'),
  [padronesEnum.CASA_DE_EMPENIO]: generateRetrieve('empresas/casas-de-empenio'),
  [padronesEnum.NOTARIO]: generateRetrieve('empresas/notarios'),
  [padronesEnum.JUEGO_DE_AZAR]: generateRetrieve('empresas/juegos-de-azar'),
  [padronesEnum.ALCOHOL]: generateRetrieve('empresas/alcoholes'),
  [padronesEnum.CEDULAR]: generateRetrieve('empresas/cedulares'),
  [padronesEnum.ARRENDAMIENTO]: generateRetrieve('empresas/arrendamientos'),
  [padronesEnum.HOSPEDAJE]: generateRetrieve('empresas/hospedajes'),
  [padronesEnum.AGENCIA]: generateRetrieve('empresas/agencias'),
  [padronesEnum.NOMINA]: generateRetrieve('empresas/nominas'),
  [padronesEnum.CONTRIBUYENTE]: generateRetrieve('empresas/contribuyentes'),
};

export const postPadronesMap = {
  [padronesEnum.CONTRIBUYENTE]: postContribuyente,
  [padronesEnum.EMPRESA]: postEmpresa,
  // 4: postVehiculo,
};

export const getListPadronesPrivateMap = {
  [padronesEnum.CIUDADANO]: generateGetList('cuentaunicasir/ciudadano-caja-public'),
  [padronesEnum.EMPRESA]: generateGetList('empresas/empresas'),
  [padronesEnum.PREDIO]: generateGetList('catastro/predios'),
  [padronesEnum.VEHICULO]: generateGetList('recaudacion/vehiculo'),
  [padronesEnum.HOSPEDAJE]: generateGetList('empresas/hospedajes'),
  [padronesEnum.ARRENDAMIENTO]: generateGetList('empresas/arrendamientos'),
  [padronesEnum.AGENCIA]: generateGetList('empresas/agencias'),
  [padronesEnum.NOMINA]: generateGetList('empresas/nominas'),
  [padronesEnum.ALCOHOL]: generateGetList('empresas/alcoholes'),
  [padronesEnum.CEDULAR]: generateGetList('empresas/cedulares'),
  [padronesEnum.JUEGO_DE_AZAR]: generateGetList('empresas/juegos-de-azar'),
  [padronesEnum.NOTARIO]: generateGetList('empresas/notarios'),
  [padronesEnum.CASA_DE_EMPENIO]: generateGetList('empresas/casas-de-empenio'),
  [padronesEnum.AGENCIA]: generateGetList('empresas/agencias'),
  [padronesEnum.CONTRIBUYENTE]: generateGetList('empresas/contribuyentes'),
  [padronesEnum.MOTOCICLETA]: generateGetList('recaudacion/motocicletas'),
  [padronesEnum.REMOLQUE]: generateGetList('recaudacion/remolques'),
  [padronesEnum.POLICIA_ESPECIAL]: generateGetList('empresas/contratos-de-policias'),
  [padronesEnum.INFRACCIONES]: generateGetList('recaudacion/infracciones'),
  [padronesEnum.COMERCIO_INFORMAL]: generateGetList('empresas/expedientes-de-comercio-informal'),
  [padronesEnum.MERCADO]: generateGetList('empresas/expedientes-de-mercados'),
  [padronesEnum.EXPEDIENTE_LICENCIA_FUNCIONAMIENTO]: generateGetList('empresas/expedientes-de-licencias'),
  [padronesEnum.ZONA_EXCLUSIVA]: generateGetList('empresas/zonas-exclusivas'),
  [padronesEnum.EXPEDIENTE_DE_ANUNCIO]: generateGetList('empresas/expedientes-de-anuncios'),
  [padronesEnum.VENTA_DE_ALCOHOL]: generateGetList('empresas/ventas-de-alcoholes'),
  [padronesEnum.RETENEDOR_DE_CEDULAR]: generateGetList('empresas/retenedores-cedulares'),
  [padronesEnum.RETENEDOR_DE_NOMINA]: generateGetList('empresas/retenedor-de-nominas'),
};

export const postSolicitarActualizarDatosDeContacto = async (values) => {
  try {
    const response = await API.post('/recaudacion/solicitar-actualizacion-contacto/', formatSent(values));
    message.info('Enviado correctamente', 3);
    return formatReceived(response.data);
  } catch (error) {
    _logger(error);
  }
  return null;
};

export const patchActualizarContacto = async (values) => {
  try {
    const response = await API.patch('/recaudacion/actualizar-contacto/', formatSent(values));
    message.info(response.data?.detail || 'Información actualizada correctamente', 3);
    return formatReceived(response.data);
  } catch (error) {
    _logger(error);
  }
  return null;
};

export default gettersMap;
